import type { ReactNode } from 'react';
import { X } from 'lucide-react';
import { Modal } from './Modal.tsx';
import { IconButton } from '../ui/index.ts';
import { useT } from '../i18n/index.ts';

/**
 * Right-edge drawer shared by the ops-user flows.
 *
 * Lifted out of `OpsUsersPage` when salesperson provisioning moved to the
 * invite-codes page (#2022): the drawer is now opened from two pages, and a
 * page importing a component out of another page is the wrong direction.
 * Its eyebrow still reads `ops_users.sheet.eyebrow` — the drawer belongs to
 * the ops-user domain regardless of which page triggers it.
 */
export function OpsUserSheet({
  title,
  titleId,
  testId,
  onClose,
  children,
}: {
  readonly title: string;
  readonly titleId: string;
  readonly testId: string;
  readonly onClose: () => void;
  readonly children: ReactNode;
}): JSX.Element {
  const t = useT();
  return (
    <Modal
      isOpen
      onClose={onClose}
      titleId={titleId}
      containerClassName="items-stretch justify-end px-0"
      panelClassName="h-full max-w-3xl rounded-none"
      testId={testId}
    >
      <div className="flex h-full flex-col">
        <div className="border-b border-line-soft px-6 py-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="m-0 text-label text-paper-low">{t('ops_users.sheet.eyebrow')}</p>
              <h3 id={titleId} className="m-0 mt-2 text-title text-paper">
                {title}
              </h3>
            </div>
            <IconButton onClick={onClose} label={t('common.close')}>
              <X aria-hidden="true" className="h-4 w-4" />
            </IconButton>
          </div>
        </div>
        {children}
      </div>
    </Modal>
  );
}
